export const PATIENT_ACCESS_TOKEN_KEY = 'medichat_web_access_token';
export const PATIENT_REFRESH_TOKEN_KEY = 'medichat_refresh_token';
export const PATIENT_WEB_SESSION_KEY = 'medichat_web_session';
export const PATIENT_CLINICAL_CONSENT_REQUIRED_KEY = 'medichat_clinical_consent_required';
export const PATIENT_CLINICAL_CONSENT_RETURN_TO_KEY = 'medichat_clinical_consent_return_to';

export const PATIENT_WEB_AUTH_PATHS = [
  '/medichat/login',
  '/medichat/register',
  '/medichat/verify-email',
  '/medichat/forgot-password',
  '/medichat/reset-password',
  '/medichat/logout',
];

const DEFAULT_PATIENT_PATH = '/medichat/chat';

export const isPatientWebAuthPath = (path) => (
  PATIENT_WEB_AUTH_PATHS.includes((path || '').replace(/\/$/, ''))
);

export const shouldUseLiffAuthentication = ({
  hostname = '',
  search = '',
  userAgent = '',
} = {}) => (
  hostname === 'liff.line.me'
  || /\bLine\//i.test(userAgent)
  || new URLSearchParams(search).has('liff.state')
);

export const shouldResumeLinePatientSession = ({ lineToken, webAccessToken, pathname = '' } = {}) => (
  !webAccessToken && (Boolean(lineToken) || pathname === '/payment' || pathname.startsWith('/payment/'))
);

export const normalizePatientReturnTo = (value) => {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return DEFAULT_PATIENT_PATH;
  }

  let decodedPath;
  try {
    decodedPath = decodeURIComponent(value.split('?')[0].split('#')[0]);
    decodedPath = decodeURIComponent(decodedPath);
  } catch (_) {
    return DEFAULT_PATIENT_PATH;
  }
  if (decodedPath.includes('\\') || decodedPath.includes('\0') || decodedPath.split('/').includes('..')) {
    return DEFAULT_PATIENT_PATH;
  }

  const normalizedUrl = new URL(value, 'https://patient.medichat.invalid');
  const path = normalizedUrl.pathname.replace(/\/$/, '');
  if (
    path.startsWith('/doctor')
    || isPatientWebAuthPath(path)
    || (
      path !== '/medichat'
      && !path.startsWith('/medichat/')
      && path !== '/payment'
      && !path.startsWith('/payment/')
    )
  ) {
    return DEFAULT_PATIENT_PATH;
  }

  return `${normalizedUrl.pathname}${normalizedUrl.search}${normalizedUrl.hash}`;
};

export const loadPatientWebSession = () => {
  try {
    const accessToken = localStorage.getItem(PATIENT_ACCESS_TOKEN_KEY);
    const isWebSession = localStorage.getItem(PATIENT_WEB_SESSION_KEY) === '1';
    if (!accessToken || !isWebSession) return null;
    const consentReturnTo = localStorage.getItem(PATIENT_CLINICAL_CONSENT_RETURN_TO_KEY);

    return {
      access_token: accessToken,
      clinical_email_consent_required:
        localStorage.getItem(PATIENT_CLINICAL_CONSENT_REQUIRED_KEY) === '1',
      ...(consentReturnTo ? { clinical_email_consent_return_to: consentReturnTo } : {}),
    };
  } catch (_) {
    return null;
  }
};

export const savePatientWebSession = (session) => {
  if (!session?.access_token) {
    throw new Error('アクセストークンがありません。');
  }

  localStorage.setItem(PATIENT_ACCESS_TOKEN_KEY, session.access_token);
  localStorage.setItem(PATIENT_WEB_SESSION_KEY, '1');
  if (session.user?.clinical_email_consent_required) {
    localStorage.setItem(PATIENT_CLINICAL_CONSENT_REQUIRED_KEY, '1');
  } else {
    localStorage.removeItem(PATIENT_CLINICAL_CONSENT_REQUIRED_KEY);
    localStorage.removeItem(PATIENT_CLINICAL_CONSENT_RETURN_TO_KEY);
  }
  localStorage.removeItem(PATIENT_REFRESH_TOKEN_KEY);
  try {
    window.dispatchEvent(new CustomEvent('medichat:patient-web-session-updated'));
  } catch (_) {}
};

export const clearPatientWebSession = () => {
  try {
    localStorage.removeItem(PATIENT_ACCESS_TOKEN_KEY);
    localStorage.removeItem(PATIENT_REFRESH_TOKEN_KEY);
    localStorage.removeItem(PATIENT_WEB_SESSION_KEY);
    localStorage.removeItem(PATIENT_CLINICAL_CONSENT_REQUIRED_KEY);
    localStorage.removeItem(PATIENT_CLINICAL_CONSENT_RETURN_TO_KEY);
  } catch (_) {}
};

export const markPatientClinicalConsentRequired = () => {
  try {
    localStorage.setItem(PATIENT_CLINICAL_CONSENT_REQUIRED_KEY, '1');
    const existingReturnTo = localStorage.getItem(PATIENT_CLINICAL_CONSENT_RETURN_TO_KEY);
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const returnTo = isPatientWebAuthPath(window.location.pathname)
      ? normalizePatientReturnTo(existingReturnTo)
      : normalizePatientReturnTo(currentPath);
    localStorage.setItem(PATIENT_CLINICAL_CONSENT_RETURN_TO_KEY, returnTo);
    window.dispatchEvent(new CustomEvent('medichat:patient-clinical-consent-required'));
  } catch (_) {}
};

export const rememberPatientClinicalConsentReturnTo = (value) => {
  try {
    localStorage.setItem(
      PATIENT_CLINICAL_CONSENT_RETURN_TO_KEY,
      normalizePatientReturnTo(value),
    );
  } catch (_) {}
};
